import React, { useEffect, useRef } from 'react';
import HonorPage from "./honorPage";
import './index.css';
import { useTranslation } from 'react-i18next';
import {
  BankOutlined,
  AimOutlined,
  TeamOutlined,
  RiseOutlined,
} from '@ant-design/icons';
import SEO from '../seo';
import { PAGE_SEO, COMMON_KEYWORDS } from '../seo/pageSeo';

const timelineData = [
  {
    year: '2012',
    title_zh: '公司成立',
    title_en: 'Company Founded',
    title_vi: 'Thành lập công ty',
    text_zh: '东莞小麦自动化科技有限公司正式成立，落户于广东省东莞市寮步镇，专注于自动化纺织设备领域。',
    text_en: 'Dongguan Xiaomai Automation Technology Co., Ltd. officially established in Dongguan, Guangdong, specializing in automated textile equipment.',
    text_vi: 'Công ty TNHH Công nghệ Tự động hóa Xiaomai Dongguan chính thức thành lập tại Dongguan, Quảng Đông, chuyên về thiết bị dệt tự động.',
  },
  {
    year: '2016',
    title_zh: '业务扩展',
    title_en: 'Business Expansion',
    title_vi: 'Mở rộng kinh doanh',
    text_zh: '产品线不断完善，市场覆盖全国各省，客户群体持续扩大，逐步成为国内铺布机领域的重要供应商。',
    text_en: 'Product line continuously improved, market coverage expanded nationwide, becoming an important supplier in the fabric spreading machine sector.',
    text_vi: 'Dòng sản phẩm không ngừng hoàn thiện, phạm vi thị trường mở rộng toàn quốc, dần trở thành nhà cung cấp quan trọng trong lĩnh vực máy trải vải.',
  },
  {
    year: '2019',
    title_zh: '战略升级',
    title_en: 'Strategic Upgrade',
    title_vi: 'Nâng cấp chiến lược',
    text_zh: '被招商引资至江西省龙南市，成立龙南小麦自动化科技有限公司，投资总额达1.2亿元人民币，生产规模大幅提升。',
    text_en: 'Invested and established Longnan Xiaomai Automation Technology Co., Ltd. in Jiangxi Province with total investment of 1.2 billion RMB, significantly expanding production capacity.',
    text_vi: 'Được đầu tư và thành lập Công ty TNHH Công nghệ Tự động hóa Longnan Xiaomai tại tỉnh Jiangxi với tổng đầu tư 1.2 tỷ RMB, năng lực sản xuất được mở rộng đáng kể.',
  },
];

const cultureData = [
  {
    icon: <AimOutlined />,
    title_zh: '企业使命',
    title_en: 'Mission',
    title_vi: 'Sứ mệnh',
    text_zh: '以技术创新驱动制造业升级，让每一台铺布机都成为客户降本增效的可靠伙伴。',
    text_en: 'Driving manufacturing upgrades through technological innovation, making every fabric spreading machine a reliable partner for customers to reduce costs and increase efficiency.',
    text_vi: 'Thúc đẩy nâng cấp sản xuất công nghiệp thông qua đổi mới công nghệ, biến mỗi máy trải vải thành đối tác đáng tin cậy giúp khách hàng giảm chi phí và tăng hiệu quả.',
  },
  {
    icon: <TeamOutlined />,
    title_zh: '团队理念',
    title_en: 'Team Philosophy',
    title_vi: 'Triết lý nhóm',
    text_zh: '尊重人才、鼓励创新，打造一支兼具工程经验与服务意识的专业团队，为客户提供全方位支持。',
    text_en: 'Respecting talent and encouraging innovation, building a professional team with engineering experience and service awareness to provide comprehensive support.',
    text_vi: 'Tôn trọng nhân tài và khuyến khích đổi mới, xây dựng đội ngũ chuyên nghiệp có kinh nghiệm kỹ thuật và ý thức dịch vụ để cung cấp hỗ trợ toàn diện.',
  },
  {
    icon: <RiseOutlined />,
    title_zh: '发展愿景',
    title_en: 'Vision',
    title_vi: 'Tầm nhìn',
    text_zh: '立足中国，走向世界，成为全球领先的铺布机制造商与自动化解决方案提供商。',
    text_en: 'Based in China, going global — becoming a world-leading fabric spreading machine manufacturer and automation solutions provider.',
    text_vi: 'Dựa Trung Quốc, vươn ra toàn cầu — trở thành nhà sản xuất máy trải vải hàng đầu thế giới và nhà cung cấp giải pháp tự động hóa.',
  },
  {
    icon: <BankOutlined />,
    title_zh: '核心价值观',
    title_en: 'Core Values',
    title_vi: 'Giá trị cốt lõi',
    text_zh: '诚信务实、精益求精、合作共赢 — 始终将产品质量与客户服务放在首位。',
    text_en: 'Integrity, pragmatism, and continuous improvement — always putting product quality and customer service first.',
    text_vi: 'Chính trực, thực tiễn và không ngừng cải tiến — luôn đặt chất lượng sản phẩm và dịch vụ khách hàng lên hàng đầu.',
  },
];

export default function About() {
  const { t, i18n } = useTranslation();
  const timelineRef = useRef(null);

  const getText = (item, field) => {
    const lang = i18n.language;
    if (lang.startsWith('vi')) return item[`${field}_vi`] || item[`${field}_zh`];
    if (lang.startsWith('en')) return item[`${field}_en`] || item[`${field}_zh`];
    return item[`${field}_zh`];
  };

  useEffect(() => {
    const items = timelineRef.current?.querySelectorAll('.about-timeline__item');
    if (!items) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, i) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              entry.target.classList.add('in-view');
            }, i * 120);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );
    items.forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, []);

  return (
    <>
    <SEO
        pageKey="about"
        title={PAGE_SEO.about.title}
        description={PAGE_SEO.about.description}
        keywords={COMMON_KEYWORDS}
        slug={PAGE_SEO.about.slug}
    />
    <div className="about-page">
      {/* Hero */}
      <div className="about-hero">
        <img
          className="about-hero__img"
          src="/files/images/companydoor.jpg"
          alt="company"
        />
        <div className="about-hero__overlay" />
        <div className="about-container about-hero__content">
          <div className="about-hero__eyebrow">XIAOMAI</div>
          <h1 className="about-hero__title">{t('companyIntro')}</h1>
          <div className="about-hero__line" />
        </div>
      </div>

      <div className="about-container">
        {/* 企业简介 */}
        <section className="about-section">
          <div className="about-section__header">
            <div className="about-section__tag">{t('about_profile_tag')}</div>
            <h2 className="about-section__title">{t('about_profile_title')}</h2>
            <div className="about-section__rule" />
          </div>

          <div className="about-desc">
            <div className="about-desc__text">
              <p>{t('companyDesc')}</p>
              <p>{t('companyDescContent')}</p>
            </div>
            <div className="about-desc__highlight">
              <div className="about-desc__highlight-num">1.2<span style={{ fontSize: '28px', fontWeight: '400', color: 'rgba(0,0,0,0.4)', marginLeft: '2px' }}>亿</span></div>
              <div className="about-desc__highlight-label">{t('about_investment_label')}</div>
              <div style={{ marginTop: '24px', borderTop: '1px solid rgba(0,0,0,0.08)', paddingTop: '20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '28px', fontWeight: '800', color: 'var(--color-primary)', letterSpacing: '-0.02em' }}>15000<span style={{ fontSize: '13px', fontWeight: '400', color: 'rgba(0,0,0,0.4)', marginLeft: '2px' }}>m²</span></div>
                    <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '10px', fontWeight: '600', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(0,0,0,0.4)', marginTop: '4px' }}>{t('about_production_area_label')}</div>
                  </div>
                  <div>
                    <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '28px', fontWeight: '800', color: 'var(--color-primary)', letterSpacing: '-0.02em' }}>2012</div>
                    <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '10px', fontWeight: '600', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(0,0,0,0.4)', marginTop: '4px' }}>{t('about_est_year_label')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 发展历程 */}
        <section className="about-section">
          <div className="about-section__header">
            <div className="about-section__tag">{t('about_milestones_tag')}</div>
            <h2 className="about-section__title">{t('about_milestones_title')}</h2>
            <div className="about-section__rule" />
          </div>

          <div className="about-timeline" ref={timelineRef}>
            {timelineData.map((item, index) => (
              <div key={index} className="about-timeline__item">
                <div className="about-timeline__year">
                  <div className="about-timeline__year-num">{item.year}</div>
                </div>
                <div className="about-timeline__marker" />
                <div className="about-timeline__card">
                  <div className="about-timeline__card-title">
                    {getText(item, 'title')}
                  </div>
                  <p className="about-timeline__card-text">
                    {getText(item, 'text')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* 企业文化 */}
        <section className="about-section">
          <div className="about-section__header">
            <div className="about-section__tag">{t('about_values_tag')}</div>
            <h2 className="about-section__title">{t('about_values_title')}</h2>
            <div className="about-section__rule" />
          </div>

          <div className="about-culture">
            {cultureData.map((item, index) => (
              <div key={index} className="about-culture__card">
                <div className="about-culture__card-icon">{item.icon}</div>
                <div className="about-culture__card-title">
                  {getText(item, 'title')}
                </div>
                <p className="about-culture__card-text">
                  {getText(item, 'text')}
                </p>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* 荣誉资质 */}
      <section className="about-section" style={{ background: '#fafafa' }}>
        <div className="about-container">
          <div className="about-section__header">
            <div className="about-section__tag">{t('about_certifications_tag')}</div>
            <h2 className="about-section__title">{t('about_certifications_title')}</h2>
            <div className="about-section__rule" />
          </div>
          <HonorPage />
        </div>
      </section>
    </div>
    </>
  );
}
